import type { IVariantProductVariant } from '../types';
import { deepCopy } from '~/utils';
import { create } from 'zustand';

export interface VariantProductVariantStore {
  variantProductVariants: IVariantProductVariant[];
  setVariantProductVariants: (variantProductVariants: IVariantProductVariant[]) => void;
  getVariantProductVariants: () => IVariantProductVariant[];
  findVariantProductVariantById: (
    id?: IVariantProductVariant['id'],
  ) => IVariantProductVariant | undefined;
  findVariantProductVariantByVariantIdAndBaseId: (
    variantID?: IVariantProductVariant['variantID'],
    baseID?: IVariantProductVariant['baseID'],
  ) => IVariantProductVariant | undefined;

  getVariantProductVariantByVariantId: (
    variantID?: IVariantProductVariant['variantID'],
  ) => IVariantProductVariant[] | undefined;

  findVariantProductVariantByBaseId: (
    baseID?: IVariantProductVariant['baseID'],
  ) => IVariantProductVariant | undefined;

  removeVariantProductVariantById: (id: IVariantProductVariant['id']) => void;
  appendVariantProductVariants: (newVariantProductVariants: IVariantProductVariant[]) => void;
  upsertVariantProductVariant: (variantProductVariant: IVariantProductVariant) => void;
}

export const useVariantProductVariantStore = create<VariantProductVariantStore>()((set, get) => ({
  variantProductVariants: [],
  setVariantProductVariants: (variantProductVariants) => set({ variantProductVariants }),
  getVariantProductVariants: () => {
    return deepCopy(get().variantProductVariants);
  },
  findVariantProductVariantById: (id) => {
    if (!id) return;

    const variantProductVariants = deepCopy(get().variantProductVariants);
    return variantProductVariants ? variantProductVariants.find((c) => c.id === id) : undefined;
  },
  getVariantProductVariantByVariantId: (variantID) => {
    if (!variantID) return;

    const variantProductVariants = deepCopy(get().variantProductVariants);
    return variantProductVariants
      ? variantProductVariants.filter((c) => c.variantID === variantID)
      : undefined;
  },

  findVariantProductVariantByBaseId: (baseID) => {
    if (!baseID) return;

    const variantProductVariants = deepCopy(get().variantProductVariants);
    return variantProductVariants?.find((c) => c.baseID === baseID);
  },

  findVariantProductVariantByVariantIdAndBaseId: (variantID, baseID) => {
    if (!variantID || !baseID) return;

    const variantProductVariants = deepCopy(get().variantProductVariants);
    return variantProductVariants?.find((c) => c.variantID === variantID && c.baseID === baseID);
  },

  appendVariantProductVariants: (newVariantProductVariants) => {
    const currentData = deepCopy(get().variantProductVariants) || [];
    const newData = [...currentData, ...newVariantProductVariants];
    set({ variantProductVariants: newData });
  },
  upsertVariantProductVariant: (variantProductVariant) => {
    const currentData = deepCopy(get().variantProductVariants) || [];
    const index = currentData.findIndex((c) => c.id === variantProductVariant.id);

    let dataUpdated;
    if (index !== -1) {
      // Update existing variantProductVariant
      dataUpdated = [...currentData];
      dataUpdated[index] = { ...dataUpdated[index], ...variantProductVariant };
    } else {
      // Insert new variantProductVariant
      dataUpdated = [...currentData, variantProductVariant];
    }

    set({ variantProductVariants: dataUpdated });
  },
  removeVariantProductVariantById: (id) => {
    const variantProductVariants = deepCopy(get().variantProductVariants) || [];
    set({ variantProductVariants: variantProductVariants.filter((item) => item.id !== id) });
  },
}));
